import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { rechargeMovilSchema, rechargeNautaSchema } from '../../utils/schemas';
import useAuthContext from '../../hooks/useAuthContext';
import { cleanNum, regex } from '../../utils/common';
import Select from './rechargeElements/Select';
import queryRecharge from '../../services/recharge/queryRecharge';
import Checkout from '../Payment/Checkout';
import ConfirmRecharge from '../Payment/ConfirmRecharge';
import Modal from '../../components/Layout/Modal';
// import SelectCurrency from '../Payment/elements/SelectCurrency';
import Multiselect from '../Common/Multiselect';
import queryContacts from '../../services/contacts/queryContacts';
import PhoneIcon from '../Common/Icons/PhoneIcon';
import TicketIcon from '../Common/Icons/TicketIcon';
import ContactInlineSelectorField from '../Common/ContactInlineSelectorField';
import {
  useDisclosure,
  Box,
  VStack,
  HStack,
  Button,
  Text,
  // Icon
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import LoginModal from '../Auth/LoginModal';
import usePendingRecharge, { encodePr, decodePr } from '../../hooks/usePendingRecharge';
import PromoBanner from './rechargeElements/PromoBanner';
import queryOffers from '../../services/offers_products/queryOffers';
import { useNavigate, useLocation } from 'react-router-dom';
import { EMAIL_CONFIRMATION } from '../../routes/app/paths';
import GradientBackground from '../Common/Background/GradientBackground';
import Spin from '../Common/loaders/Spin';
import PaymentSuccessContent from '../Payment/PaymentSuccessContent';

// const customStyles = {
//   selected: 'text-white bg-blue-800 active:bg-blue-primary/80',
//   unSelected: 'border border-blue-primary text-blue-primary bg-green-primary',
// };

// query keys to store in global cache
const qkRechargeMovil = ['main-recharge', 'movil']
const qkRechargeNauta = ['main-recharge', 'nauta']

export { RechargeSkeleton } from './RechargeSkeleton';

export default function Recharge({ expandContacts = false, maxVisibleContacts }) {
  /* Hooks */
  const { isAuthenticated, user, preferences } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    savePendingContacts, getPendingContacts, clearPendingContacts,
    getPendingProduct, clearPendingProduct,
  } = usePendingRecharge();
  const { t } = useTranslation();
  const {
    isOpen: isOpenSuccess,
    onOpen: onOpenSuccess,
    onClose: onCloseSuccess,
  } = useDisclosure();
  const {
    isOpen: isOpenLogin,
    onOpen: onOpenLogin,
    onClose: onCloseLogin,
  } = useDisclosure();

  /* Global cache */
  const queryClient = useQueryClient();
  const movilCached = queryClient.getQueryData(qkRechargeMovil);
  const nautaCached = queryClient.getQueryData(qkRechargeNauta);

  /* Forms */
  const movilForm = useForm({
    resolver: yupResolver(rechargeMovilSchema),
    defaultValues: movilCached ? {
      ...movilCached,
    } : {
      currency: preferences?.currency || 'eur',
      contacts: [],
    },
  });
  const nautaForm = useForm({
    resolver: yupResolver(rechargeNautaSchema),
    defaultValues: nautaCached ? {
      ...nautaCached,
    } : {
      currency: preferences?.currency || 'eur',
      contacts: [],
    },
  });

  /* Queries */
  const { data: dataMovil, isLoading: isLoadingMovil, refetch: refetchMovil } =
    queryRecharge().listRechargeByType('movil');
  const { data: dataNauta, isLoading: isLoadingNauta, refetch: refetchNauta } =
    queryRecharge().listRechargeByType('nauta');
  const { data: contacts, isLoading: isLoadingContacts } =
    queryContacts().getContactList();
  const { data: dataPromotion } = queryOffers().offerPublicOnly();

  /* Values memo */
  const offers = useMemo(() => {
    //get offers in movil data
    const listOffers =
      dataMovil?.lists_recharge
        ?.filter(
          (item) => item.rechargeable_type === 'OfferProduct',
        )
        ?.sort((a, b) => a.price_eur - b.price_eur) || [];
    // add schedule to offers and differentiates them
    return listOffers.concat(
      dataMovil?.lists_recharge_schedule
        ?.filter((item) => item.rechargeable_type === 'OfferProduct')
        ?.sort((a, b) => a.price_eur - b.price_eur)
        ?.map((item) => {
          return {
            ...item,
            schedule: true,
          };
        }) || [],
    );
  }, [dataMovil]);

  const optionsMovil = useMemo(
    () =>
      offers
        .concat(
          dataMovil?.lists_recharge?.filter(
            (item) => item.rechargeable_type === 'Product',
          )
            ?.sort((a, b) => a.price_eur - b.price_eur)
          || [],
        ),
    [offers, dataMovil],
  );

  const optionsNauta = useMemo(
    () =>
      dataNauta?.lists_recharge
        ?.filter(
          (item) => item.rechargeable_type === 'Product',
        )
        ?.sort((a, b) => a.price_eur - b.price_eur) || [],
    [dataNauta],
  );

  const [typeSelected, setTypeSelected] = useState('movil');
  const optionsContacts = useMemo(
    () =>
      contacts
        ?.filter((c) =>
          typeSelected === 'movil'
            ? c.phone && (() => {
              const cleanPhone = cleanNum(c.phone);
              return regex.movil.test(cleanPhone)
            })
            : c.email && regex.nauta.test(c.email),
        )
        ?.map((contact) => {
          const phone = contact.phone && cleanNum(contact.phone);
          return {
            name: contact.name,
            value: typeSelected === 'movil' ? phone : contact.email,
            phone,
            email: contact.email,
          }
        })
        .concat(
          typeSelected === 'movil'
            ? movilForm
              .watch('contacts')
              .filter(
                (item) =>
                  !contacts?.some((contact) => contact.phone === item.phone),
              )
              .map((item) => ({
                name: item.name,
                value: item.phone,
                phone: item.phone,
              }))
            : nautaForm
              .watch('contacts')
              .filter(
                (item) =>
                  !contacts?.some((contact) => contact.email === item.email),
              )
              .map((item) => ({
                name: item.name,
                value: item.email,
                email: item.email,
              })),
        ) || [],
    [contacts, typeSelected],
  );

  const currentPromotion = useMemo(() => {
    if (!dataPromotion) return null;
    return {
      title: dataPromotion.name,
      description: dataPromotion.description,
      rangeDate: {
        init: dataPromotion.date_init,
        end: dataPromotion.date_end,
      },
      future: new Date(dataPromotion.date_init) > new Date(),
    }
  }, [dataPromotion]);

  /* States */
  const [selectedMovil, setSelectedMovil] = useState(
    optionsMovil.find(o => o.rechargeable_id === movilCached?.rechargeable_id) ||
    optionsMovil[0] || {});
  const [selectedNauta, setSelectedNauta] = useState(
    optionsNauta.find(o => o.rechargeable_id === nautaCached?.rechargeable_id) ||
    optionsNauta[0] || {});
  const [showConfirmRecharge, setShowConfirmRecharge] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [recharge, setRecharge] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const [resetMultiSelect, setResetMultiSelect] = useState(false);
  const [loginPr, setLoginPr] = useState(null);

  // Leer pr pendiente: 1) state de navegación (desde EmailConfirmation), 2) URL param, 3) encryptedStorage (desde Item.jsx)
  const [pendingPr] = useState(() => {
    const statePr = location.state?.pr;
    if (statePr) return statePr;
    try {
      const urlPr = new URLSearchParams(window.location.search).get('pr');
      if (urlPr) return urlPr;
    } catch { /* noop */ }
    return getPendingProduct() || null;
  });

  /* RQ - recharge create (after recharge state is declared) */
  const { data: checkoutDataQuery, isLoading: isCheckoutLoading } = queryRecharge().rechargeCreate(recharge);

  /* Effects */
  useEffect(() => {
    if (movilCached?.contacts?.length) {
      setTypeSelected('movil');
    }
    if (nautaCached?.contacts?.length) {
      setTypeSelected('nauta');
    }
    refetchMovil();
    refetchNauta();
  }, []);

  // Set default currency from user preferences once they load (async)
  useEffect(() => {
    if (!preferences?.currency) return;
    if (!movilCached) movilForm.setValue('currency', preferences.currency);
    if (!nautaCached) nautaForm.setValue('currency', preferences.currency);
  }, [preferences?.currency]);

  useEffect(() => {
    if (checkoutDataQuery?.id) {
      setCheckoutData(checkoutDataQuery);
      if (pendingConfirm) {
        setPendingConfirm(false);
        setShowConfirmRecharge(true);
      }
    }
  }, [checkoutDataQuery, pendingConfirm]);

  useEffect(() => {
    if (!optionsMovil.length) return;
    if (!Object.keys(selectedMovil).length) setSelectedMovil(optionsMovil[0]);
  }, [optionsMovil, selectedMovil]);

  useEffect(() => {
    if (!optionsNauta.length) return;
    if (!Object.keys(selectedNauta).length) setSelectedNauta(optionsNauta[0]);
  }, [optionsNauta, selectedNauta]);

  useEffect(() => { // set data stored in global cache
    if (typeSelected === 'movil') {
      movilForm.setValue('rechargeable_id', selectedMovil.rechargeable_id);
      movilForm.setValue('rechargeable_type', selectedMovil.rechargeable_type);
    } else {
      nautaForm.setValue('rechargeable_id', selectedNauta.rechargeable_id);
      nautaForm.setValue('rechargeable_type', selectedNauta.rechargeable_type);
    }
  }, [selectedMovil, selectedNauta, typeSelected]);

  // Restaurar selección pendiente tras flujo de registro + verificación de email
  useEffect(() => {
    if (!pendingPr) return;
    const allOptions = [...optionsMovil, ...optionsNauta];
    if (!allOptions.length) return;

    const decoded = decodePr(pendingPr);
    if (!decoded) return;

    const matchMovil = optionsMovil.find(o => o.rechargeable_id === decoded.rechargeable_id);
    const matchNauta = optionsNauta.find(o => o.rechargeable_id === decoded.rechargeable_id);
    const match = matchMovil || matchNauta;
    if (!match) return;

    if (matchMovil) {
      setSelectedMovil(matchMovil);
      setTypeSelected('movil');
    } else {
      setSelectedNauta(matchNauta);
      setTypeSelected('nauta');
    }

    // Restaurar contactos si aún están vigentes (TTL)
    const pendingContacts = getPendingContacts();
    if (pendingContacts?.length) {
      const form = matchMovil ? movilForm : nautaForm;
      form.setValue('contacts', pendingContacts);
    }
    clearPendingContacts();
    clearPendingProduct();
  }, [optionsMovil, optionsNauta, pendingPr]);

  /* Functions */
  const getCurrentForm = (type = typeSelected) =>
    type === 'movil' ? movilForm : nautaForm;

  const getContactsError = (type = typeSelected) => {
    const error = getCurrentForm(type).formState.errors.contacts;

    if (!error) return '';
    if (Array.isArray(error)) return error[0]?.phone?.message || error[0]?.email?.message || '';

    return error.message || '';
  };

  const onSubmit = (e) => {
    if (!user.email_verified_at) {
      return navigate(EMAIL_CONFIRMATION);
    }

    const data = {};
    if (typeSelected === 'movil') {
      data.rechargeable_id = selectedMovil.rechargeable_id;
      data.rechargeable_type = selectedMovil.rechargeable_type;
    } else {
      data.rechargeable_id = selectedNauta.rechargeable_id;
      data.rechargeable_type = selectedNauta.rechargeable_type;
    }
    data.contacts = e.contacts;
    data.currency = e?.currency;
    data.cost_points = typeSelected === 'movil' ? selectedMovil.cost_points : selectedNauta.cost_points;

    setRecharge(data);
    setPendingConfirm(true);
  };

  const handleOnChange = (data) => {
    if (typeSelected === 'movil') {
      movilForm.setValue('currency', data?.value || 'eur');

      // store data in global cache
      queryClient.setQueryData(qkRechargeMovil, {
        ...movilForm.getValues(),
        currency: data?.value || 'eur',
      }, { staleTime: Infinity });
    }
    nautaForm.setValue('currency', data?.value || 'eur');

    // store data in global cache
    queryClient.setQueryData(qkRechargeNauta, {
      ...nautaForm.getValues(),
      currency: data?.value || 'eur',
    }, { staleTime: Infinity });
  };

  const handleSelectProduct = (newValue) => {
    if (typeSelected === 'movil') {
      setSelectedMovil(newValue);

      // store data in global cache
      queryClient.setQueryData(qkRechargeMovil, {
        ...movilForm.getValues(),
        rechargeable_id: newValue.rechargeable_id,
        rechargeable_type: newValue.rechargeable_type,
      }, { staleTime: Infinity });

    } else {
      setSelectedNauta(newValue);

      // store data in global cache
      queryClient.setQueryData(qkRechargeNauta, {
        ...nautaForm.getValues(),
        rechargeable_id: newValue.rechargeable_id,
        rechargeable_type: newValue.rechargeable_type,
      }, { staleTime: Infinity });
    }
  };

  const handleNautaMultiselectOnChange = (data) => {
    if (data.length === 0 && nautaForm.watch('contacts').length === 0) return;

    const nautaContacts = data.map((item) => ({
      email: item.value,
      name: item.name,
      value: item.value,
    }));

    nautaForm.setValue(
      'contacts',
      nautaContacts,
    );

    if (data.length) {
      queryClient.setQueryData(qkRechargeNauta, {
        ...nautaForm.getValues(),
        contacts: nautaContacts,
      }, { staleTime: Infinity });
    }

    nautaForm.trigger('contacts');
  };


  return (
    <>
      <GradientBackground vertical fadeToTransparent color1='#EAF1F1' color2='#B3B8B8' className="px-8 rounded-3xl flex flex-col gap-2 shadow-xl backdrop-blur-sm z-10">
        <Box
          className='flex flex-col gap-5 w-full max-w-2xl rounded-[2rem] px-3 md:px-6 py-5 md:py-6'
          as="form"
          onSubmit={
            typeSelected === 'movil'
              ? movilForm.handleSubmit(onSubmit)
              : nautaForm.handleSubmit(onSubmit)
          }
        >
          <VStack spacing={5} mt={2} mb={3}>
            <Text
              fontSize={{ base: "3xl", md: "4xl" }}
              // fontWeight="bold"
              color="#187672"
              letterSpacing="tight"
              textAlign="center"
              fontFamily='"Brave Hunter", sans-serif'
            >
              {t('recharge.heading')}
            </Text>

            {currentPromotion && (
              <Box display="flex" justifyContent="center" w="full">
                <PromoBanner
                  info={currentPromotion}
                  timingScale={0.72}
                />
              </Box>
            )}

            <HStack
              spacing={0}
              bg="#187672"
              borderRadius="full"
              p={1}
            >
              <Button
                onClick={() => setTypeSelected('movil')}
                borderRadius="full"
                px={{ base: 6, sm: 8 }}
                py={{ base: 2, sm: 3 }}
                bg={typeSelected === 'movil' ? 'white' : 'transparent'}
                color={typeSelected === 'movil' ? 'teal.600' : 'white'}
                fontWeight="medium"
                fontSize={{ base: "sm", sm: "md" }}
                _hover={{
                  bg: typeSelected === 'movil' ? 'gray.50' : 'teal.700'
                }}
                transition="all 0.2s"
              >
                {t('recharge.selectMobile')}
              </Button>
              <Button
                onClick={() => setTypeSelected('nauta')}
                borderRadius="full"
                px={{ base: 6, sm: 8 }}
                py={{ base: 2, sm: 3 }}
                bg={typeSelected === 'nauta' ? 'white' : 'transparent'}
                color={typeSelected === 'nauta' ? 'teal.600' : 'white'}
                fontWeight="medium"
                fontSize={{ base: "sm", sm: "md" }}
                _hover={{
                  bg: typeSelected === 'nauta' ? 'gray.50' : 'teal.700'
                }}
                transition="all 0.2s"
              >
                {t('recharge.selectNauta')}
              </Button>
            </HStack>
          </VStack>

          {/* Campos del formulario */}
          <VStack spacing={4} w="full">
            <Box w="full" display={typeSelected === 'movil' ? 'block' : 'none'}>
              <ContactInlineSelectorField
                form={movilForm}
                type="movil"
                isLoading={isLoadingContacts && isAuthenticated}
                options={optionsContacts}
                placeholder="Ej: +53 5XXX XXXX"
                icon={<PhoneIcon w="14px" h="24px" />}
                expandAll={expandContacts}
                maxVisible={maxVisibleContacts}
                onContactsChange={(nextContacts) => {
                  queryClient.setQueryData(qkRechargeMovil, {
                    ...movilForm.getValues(),
                    contacts: nextContacts,
                  }, { staleTime: Infinity });
                }}
              />
            </Box>

            <Box w="full" display={typeSelected !== 'movil' ? 'block' : 'none'}>
              <Multiselect
                type="email"
                isLoading={isLoadingContacts && isAuthenticated}
                initialSelected={nautaCached?.contacts || []}
                listOptions={optionsContacts}
                onChange={handleNautaMultiselectOnChange}
                reset={{
                  value: resetMultiSelect,
                  setValue: setResetMultiSelect,
                }}
                icon={<PhoneIcon w="14px" h="24px" />}
              />
              {getContactsError('nauta') ? (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {getContactsError('nauta')}
                </Text>
              ) : null}
            </Box>


            <Box w="full">
              {typeSelected === 'movil' ? (
                <Select
                  value={selectedMovil}
                  setValue={handleSelectProduct}
                  options={optionsMovil}
                  isLoading={isLoadingMovil}
                  currency={movilForm.watch('currency')}
                  icon={<TicketIcon w="24px" h="24px" />}
                />
              ) : (
                <Select
                  value={selectedNauta}
                  setValue={handleSelectProduct}
                  options={optionsNauta}
                  isLoading={isLoadingNauta}
                  currency={nautaForm.watch('currency')}
                  icon={<TicketIcon w="24px" h="24px" />}
                />
              )}
            </Box>

            <motion.div
              layout
              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
              style={{ width: '100%', overflow: 'hidden' }}
            >
              <VStack
                spacing={1}
                w="full"
                mt={2}
                bg="gray.50"
                border="1.5px solid"
                borderColor="green.500"
                borderRadius="xl"
                px={4}
                py={3}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    layout
                    key={typeSelected === 'movil' ? selectedMovil.description : selectedNauta.description}
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={{ width: '100%' }}
                  >
                    {(typeSelected === 'movil' ? selectedMovil?.rechargeable_type : selectedNauta?.rechargeable_type) === 'OfferProduct' ? (
                      <HStack spacing={3} w="full" align="flex-start">
                        <Text fontSize="xl" lineHeight="1.3" mt="1px">⚡</Text>
                        <VStack spacing={0} align="flex-start" flex={1}>
                          <Text color="teal.700" fontWeight="bold" fontSize="sm">
                            {t('recharge.activeBonus')}
                          </Text>
                          <Text color="gray.600" fontWeight="normal" fontSize="sm">
                            {typeSelected === 'movil' ? selectedMovil.description : selectedNauta.description}
                          </Text>
                        </VStack>
                      </HStack>
                    ) : (
                      <Text color="teal.700" fontWeight="semibold" textAlign="center" fontSize="sm">
                        {typeSelected === 'movil' ? selectedMovil.description : selectedNauta.description}
                      </Text>
                    )}
                  </motion.div>
                </AnimatePresence>

                <AnimatePresence>
                  {((selectedMovil?.point > 0 && typeSelected === 'movil') || (selectedNauta?.point > 0 && typeSelected !== 'movil')) && (
                    <motion.div
                      layout
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                      style={{ overflow: 'hidden', width: '100%', textAlign: 'center' }}
                    >
                      <Text color="teal.700" fontSize="xs" fontWeight="semibold" textAlign="center">
                        {t('recharge.earnPoints', {
                          points:
                            typeSelected === 'movil'
                              ? Number(selectedMovil.point * (movilForm.watch('contacts')?.length || 1)).toFixed(2)
                              : Number(selectedNauta.point * (nautaForm.watch('contacts')?.length || 1)).toFixed(2),
                        })}
                      </Text>
                    </motion.div>
                  )}
                </AnimatePresence>
              </VStack>
            </motion.div>

            <Box w="full" textAlign="center" mt={4}>
              {!isAuthenticated ? (
                <Button
                  type="button"
                  onClick={() => {
                    const selected = typeSelected === 'movil' ? selectedMovil : selectedNauta;
                    if (selected?.rechargeable_id) {
                      const currentContacts = getCurrentForm().getValues('contacts');
                      if (currentContacts?.length) {
                        savePendingContacts(currentContacts);
                      }
                      setLoginPr(encodePr(selected.rechargeable_id, selected.rechargeable_type));
                    }
                    onOpenLogin();
                  }}
                  borderRadius="full"
                  bg="#187672"
                  color="white"
                  px={8}
                  py={6}
                  fontSize={{ base: "md", lg: "xl" }}
                  fontWeight="medium"
                  _hover={{ bg: 'teal.800', shadow: 'lg' }}
                  _active={{ bg: 'teal.900' }}
                >
                  {t('recharge.rechargeButton')}
                </Button>
              ) : (
                <Button
                  type="submit"
                  borderRadius="full"
                  bg={
                    typeSelected === 'movil' && selectedMovil?.schedule
                      ? 'yellow.400'
                      : '#187672'
                  }
                  color={
                    typeSelected === 'movil' && selectedMovil?.schedule
                      ? 'teal.900'
                      : 'white'
                  }
                  px={8}
                  py={6}
                  fontSize={{ base: "md", lg: "xl" }}
                  fontWeight={
                    typeSelected === 'movil' && selectedMovil?.schedule
                      ? 'semibold'
                      : 'medium'
                  }
                  isDisabled={
                    typeSelected === 'movil'
                      ? isLoadingMovil || optionsMovil.length === 0
                      : isLoadingNauta || optionsNauta.length === 0
                  }
                  _hover={{
                    bg:
                      typeSelected === 'movil' && selectedMovil?.schedule
                        ? 'yellow.500'
                        : 'teal.800',
                    shadow: 'lg',
                  }}
                  _active={{
                    bg:
                      typeSelected === 'movil' && selectedMovil?.schedule
                        ? 'yellow.600'
                        : 'teal.800',
                  }}
                  _disabled={{ bg: 'gray.400' }}
                >
                  {!isLoadingMovil && !isLoadingNauta
                    ? typeSelected === 'movil' && selectedMovil?.schedule
                      ? t('recharge.scheduleRecharge')
                      : t('recharge.rechargeButton')
                    : t('recharge.loadingMessage')}
                </Button>
              )}
            </Box>
          </VStack>
        </Box>
      </GradientBackground>

      {/* Loading overlay while rechargeCreate resolves */}
      {pendingConfirm && isCheckoutLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-white px-10 py-8 shadow-xl">
            <Spin strokeWidth="2" className="w-8 h-8 text-[#187672]" />
            <span className="text-sm text-gray-500">{t('profile.history.content.preparingRecharge')}</span>
          </div>
        </div>
      )}

      {/* Confirm Recharge modal (intermediate step) */}
      <Modal
        show={showConfirmRecharge}
        onClose={() => {
          setShowConfirmRecharge(false);
          setPendingConfirm(false);
          setRecharge(null);
          setCheckoutData(null);
        }}
        size="5xl"
      >
        <ConfirmRecharge
          recharge={recharge}
          checkoutData={checkoutData}
          savedContacts={contacts}
          onClose={() => {
            setShowConfirmRecharge(false);
            setPendingConfirm(false);
            setRecharge(null);
            setCheckoutData(null);
          }}
          onSuccess={() => {
            // Guardar contactos antes de limpiar el estado — cuando el pago se
            // completa aquí (puntos / tarjeta guardada), Checkout nunca monta y
            // su useEffect no setea pending_recharge_contacts.
            if (recharge?.contacts?.length) {
              sessionStorage.setItem('pending_recharge_contacts', JSON.stringify(recharge.contacts));
            }
            setShowConfirmRecharge(false);
            setRecharge(null);
            setCheckoutData(null);
            onOpenSuccess();

            // remove data in global cache
            queryClient.removeQueries('paymentStripeIntent');
            queryClient.removeQueries(qkRechargeMovil);
            queryClient.removeQueries(qkRechargeNauta);

            // reset forms
            movilForm.reset();
            nautaForm.reset();
            setResetMultiSelect(true);
          }}
          onProceedToPayment={() => {
            setShowConfirmRecharge(false);
            setShowCheckout(true);
          }}
        />
      </Modal>

      {/* Checkout modal (Stripe payment) */}
      <Modal
        show={showCheckout}
        onClose={() => {
          setShowCheckout(false);
          setRecharge(null);
          setCheckoutData(null);
          queryClient.removeQueries('paymentStripeIntent');
        }}
        noCloserOutside
        size="none"
      >
        <Checkout
          recharge={recharge}
          prevCheckoutData={checkoutData}
          onClose={() => {
            setShowCheckout(false);
            setRecharge(null);
            setCheckoutData(null);
            queryClient.removeQueries('paymentStripeIntent');
          }}
          onSuccess={() => {
            setShowCheckout(false);
            setRecharge(null);
            setCheckoutData(null);
            onOpenSuccess();

            // remove data in global cache
            queryClient.removeQueries('paymentStripeIntent');
            queryClient.removeQueries(qkRechargeMovil);
            queryClient.removeQueries(qkRechargeNauta);

            // reset forms
            movilForm.reset();
            nautaForm.reset();
            setResetMultiSelect(true);
          }}
        />
      </Modal>

      {/* Modal de Éxito en la recarga */}
      <Modal show={isOpenSuccess} onClose={onCloseSuccess}>
        <PaymentSuccessContent onClose={onCloseSuccess} />
      </Modal>

      {/* Redirect to login */}
      <LoginModal show={isOpenLogin} onClose={onCloseLogin} pr={loginPr} />
    </>
  );
}
